import {autoserializeAs, autoserializeAsJson, autoserializeUsing} from "dcerialize";
import type {DateTime} from "luxon";
import {DateTimeSerializer} from "../../helpers/DateSerializers";
import type {IdOf} from "../base/IdentifiableModel";
import {CodeOnlyLanguage} from "../i18n/Language";
import type {PermissionId} from "./Permission";
import type {AnyUser, UserAccountType} from "./User";

/**
 * @sealed
 */
export class LoginRequest {
  @autoserializeAs(() => String)
  public readonly audience: string;
  @autoserializeAs(() => String)
  public readonly email: string;
  @autoserializeAs(() => String)
  public readonly password: string;

  public constructor(audience: string, email: string, password: string) {
    this.audience = audience;
    this.email = email;
    this.password = password;
  }
}

/**
 * @sealed
 */
export class TokenResponse {
  @autoserializeAs(() => String)
  public readonly accessToken: string;
  @autoserializeUsing(DateTimeSerializer)
  public readonly expiresAt: DateTime<true>;
  @autoserializeAs(() => Boolean)
  public readonly needsPasswordChange: boolean;
  @autoserializeAs(() => CodeOnlyLanguage)
  public readonly language: Readonly<CodeOnlyLanguage>;
  @autoserializeAs(() => Number)
  public readonly userId: IdOf<AnyUser>;
  @autoserializeAsJson()
  public readonly accountType: UserAccountType;
  @autoserializeAsJson()
  public readonly permissions: readonly PermissionId[];
}

/**
 * @sealed
 */
export class ForgotPasswordRequest {
  @autoserializeAs(() => String)
  public readonly email: string;
  @autoserializeAs(() => String)
  public readonly returnUrl?: string;

  public constructor(email: string, returnUrl?: string) {
    this.email = email;
    this.returnUrl = returnUrl;
  }
}

/**
 * @sealed
 */
export class ChangePasswordRequest {
  @autoserializeAs(() => String)
  public readonly oldPassword: string;
  @autoserializeAs(() => String)
  public readonly newPassword: string;

  public constructor(oldPassword: string, newPassword: string) {
    this.oldPassword = oldPassword;
    this.newPassword = newPassword;
  }
}

/**
 * @sealed
 */
export class ResetPasswordRequest {
  @autoserializeAs(() => String)
  public readonly email: string;
  @autoserializeAs(() => String)
  public readonly resetToken: string;
  @autoserializeAs(() => String)
  public readonly newPassword: string;

  public constructor(email: string, resetToken: string, newPassword: string) {
    this.email = email;
    this.resetToken = resetToken;
    this.newPassword = newPassword;
  }
}
